import React, {Component} from 'react';
import querystring from 'query-string';
import cookie from 'react-cookies';
import {store} from '../../../../helper/store';
import {post} from '../../../../helper/request';
import {addAnnotation} from '../../../../redux_actions/maps.state.action';
import {updateGammaState, updateMagicToolDataRetrievalStatus} from '../../../../redux_actions/gamma.state.action';
import {AnnotationsConstants} from '../../../../utils/const';
import {annotationsApp} from '../../app_maker';
import {getFormattedArea, getFormattedLength, getLoggedInUserId, getUperpixel} from '../../../../utils/utils';
import {getAnnotationsFeature, isROIAnnotation} from '../../utils/annotations_app_utils';
import {notifyCustomErrors, notifyWarning} from '../../utils/display.notification';
import {FIXED_ANNO_HEIGHT} from '../../drawer/draw_tool_keys';
import {annotationContainsPoint, closeRing, extentCenter, pathLength, ringArea} from './annotationGeometry';
import {forwardWheelToViewer, panViewerByPixels} from './viewerPointer';

/** Same ids as the OL draw tools; saved as `tool_type`. */
export const TOOL_IDS = {
    ARROW: 'Arrow',
    LINE: 'Line',
    FREELINE: 'Freeline',
    FREEHAND: 'Freehand',
    CIRCLE: 'Circle',
    RECTANGLE: 'Rectangle',
    POLYGON: 'Polygon',
    MAGIC_TOOL: 'MagicTool',
};

const CLICK_TOOLS = new Set([TOOL_IDS.LINE, TOOL_IDS.ARROW, TOOL_IDS.RECTANGLE, TOOL_IDS.POLYGON, TOOL_IDS.MAGIC_TOOL]);
// Dragging draws with these (OL freehand; Circle is drag-to-radius, see docs/0006).
const DRAG_TOOLS = new Set([TOOL_IDS.FREELINE, TOOL_IDS.FREEHAND, TOOL_IDS.CIRCLE]);

const MAX_ARROW_POINTS = 10;
const PAN_THRESHOLD_PX = 5;
const SAME_POINT_PX = 6;
const MAGIC_STEP_PX = 150;
const PREVIEW_BLUE = 'rgba(0, 153, 255, 1)';
const PREVIEW_STROKE = 3;
const PREVIEW_FILL = 'rgba(255, 255, 255, 0.5)';

const titlePrefix = () => (cookie.loadAll().superuser === 'true' ? 'A ' : 'R ');

/**
 * Drawing layer rendered inside AnnotationOverlayOSD on the active map. Points are kept in OL
 * coordinates (`toOl` / `toScreen`), so saved annotations match what the OL draw tools send
 * (BaseDrawTool.addFeatureAnnotation): same fields, units, centre (extent centre), closed rings,
 * ROI check and the extra "Label" annotation for arrows.
 *
 * Click tools keep their points while the user drag-pans between clicks, as in OL.
 */
class DrawInteractionOSD extends Component {
    constructor(props) {
        super(props);
        this._hitAreaRef = React.createRef();
        this._hitAreaWheelTarget = null;
        this._justPanned = false;
        this._panLastClient = null;
        this.state = this.initialState();
    }

    initialState = () => ({
        points: [],
        previewPoint: null,
        isDrawing: false,
        cursorScreen: null,
        mightPan: false,
        isPanning: false,
        panStart: null,
        magicFeatures: [],
        magicPreviewFeature: null,
    });

    componentDidMount() {
        this._attachWheelForwarder();
    }

    componentDidUpdate(prevProps) {
        if (prevProps.activeTool !== this.props.activeTool) this.setState(this.initialState());
        this._attachWheelForwarder();
    }

    componentWillUnmount() {
        this._hitAreaWheelTarget?.removeEventListener('wheel', this._forwardWheel);
        document.removeEventListener('mousemove', this._onPanMouseMoveDoc, true);
        document.removeEventListener('mouseup', this._onPanMouseUpDoc, true);
        clearTimeout(this._justPannedTimer);
        if (this.props.activeTool === TOOL_IDS.MAGIC_TOOL) store.dispatch(updateMagicToolDataRetrievalStatus(false));
    }

    // ------------------------------------------------------------------ helpers

    _eventScreen = (e) => {
        const rect = this._hitAreaRef.current.getBoundingClientRect();
        return [e.clientX - rect.left, e.clientY - rect.top];
    };

    _eventToOl = (e) => this.props.toOl(this._eventScreen(e));

    _screenDistance = (a, b) => {
        const [ax, ay] = this.props.toScreen(a);
        const [bx, by] = this.props.toScreen(b);
        return Math.hypot(ax - bx, ay - by);
    };

    /** Drops consecutive points closer than a few screen pixels (the extra click of a double-click). */
    _dedupe = (points) => points.filter((p, i) => i === 0 || this._screenDistance(p, points[i - 1]) >= 2);

    _reset = () => this.setState({points: [], isDrawing: false, previewPoint: null});

    /** Right-click stops drawing, as in BaseDrawTool.condition. */
    _stopDrawing = (e) => {
        e.preventDefault();
        store.dispatch(updateGammaState({activeDrawTool: null}));
    };

    _consumeJustPanned = () => {
        if (!this._justPanned) return false;
        this._justPanned = false;
        return true;
    };

    // ------------------------------------------------------------------ drag-pan between clicks

    _forwardWheel = (e) => forwardWheelToViewer(this.props.viewer, e);

    _attachWheelForwarder = () => {
        const el = this._hitAreaRef.current;
        if (!el || el === this._hitAreaWheelTarget) return;
        this._hitAreaWheelTarget?.removeEventListener('wheel', this._forwardWheel);
        el.addEventListener('wheel', this._forwardWheel, {passive: false});
        this._hitAreaWheelTarget = el;
    };

    _startPan = (e) => {
        this._panLastClient = [e.clientX, e.clientY];
        document.addEventListener('mousemove', this._onPanMouseMoveDoc, true);
        document.addEventListener('mouseup', this._onPanMouseUpDoc, true);
        this.setState(CLICK_TOOLS.has(this.props.activeTool)
            ? {isPanning: true, mightPan: false}
            : {isPanning: true, mightPan: false, points: [], isDrawing: false, previewPoint: null});
    };

    _onPanMouseMoveDoc = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (this._panLastClient) {
            panViewerByPixels(this.props.viewer, this._panLastClient[0] - e.clientX, this._panLastClient[1] - e.clientY);
        }
        this._panLastClient = [e.clientX, e.clientY];
        if (this._hitAreaRef.current) this.setState({cursorScreen: this._eventScreen(e)});
    };

    _onPanMouseUpDoc = (e) => {
        e.preventDefault();
        e.stopPropagation();
        document.removeEventListener('mousemove', this._onPanMouseMoveDoc, true);
        document.removeEventListener('mouseup', this._onPanMouseUpDoc, true);
        this._panLastClient = null;
        // The click that follows this mouseup must not place a point.
        this._justPanned = true;
        clearTimeout(this._justPannedTimer);
        this._justPannedTimer = setTimeout(() => { this._justPanned = false; }, 150);
        this.setState({isPanning: false, mightPan: false, panStart: null});
    };

    // ------------------------------------------------------------------ saving

    _roiAllows = (center) => {
        const roi = (this.props.annotations || []).find(isROIAnnotation);
        if (!roi || annotationContainsPoint(roi, center)) return true;
        notifyWarning('roi', 'The annotation is not in the region of the interest');
        return false;
    };

    /** Fields every OL draw tool sends; `center` is the OL extent centre. */
    _save = ({extent, ...fields}) => {
        const center = extentCenter(extent);
        if (!this._roiAllows(center)) return false;
        store.dispatch(addAnnotation(this.props.mapId, {
            color: this.props.selectedDrawColor,
            slide: this.props.slide.id,
            creator: getLoggedInUserId(),
            center: center.toString(),
            anno_drawer: annotationsApp.drawer,
            created_z_level: this.props.viewer.getView().getZoom(),
            meta: {width: this.props.selectedAnnoWidth},
            ...fields,
        }));
        return true;
    };

    _extentOf = (points) => {
        const xs = points.map(p => p[0]);
        const ys = points.map(p => p[1]);
        return [Math.min(...xs), Math.min(...ys), Math.max(...xs), Math.max(...ys)];
    };

    _savePolygon = (points, toolType) => {
        const ring = closeRing(this._dedupe(points));
        if (ring.length < 4) return;
        const area = ringArea(ring);
        const upx = this.props.slide.uperpixel;
        this._save({
            area: area * upx * upx,
            perimeter: pathLength(ring) * upx,
            new_bounds: JSON.stringify([ring]),
            shape: AnnotationsConstants.POLYGON,
            tool_type: toolType,
            title: titlePrefix() + getFormattedArea(area * getUperpixel() ** 2),
            extent: this._extentOf(ring),
        });
    };

    _saveLine = (points, toolType) => {
        const line = this._dedupe(points);
        if (line.length < 2) return false;
        const length = pathLength(line);
        return this._save({
            area: 0,
            perimeter: length * this.props.slide.uperpixel,
            new_bounds: JSON.stringify(line),
            shape: AnnotationsConstants.LINE,
            tool_type: toolType,
            title: titlePrefix() + getFormattedLength(length * getUperpixel()),
            extent: this._extentOf(line),
        });
    };

    _saveCircle = (center, radius) => {
        const upx = this.props.slide.uperpixel;
        const globalUpx = getUperpixel();
        this._save({
            area: Math.PI * radius * radius * upx * upx,
            perimeter: 2 * Math.PI * radius * upx,
            new_bounds: JSON.stringify([center, radius]),
            shape: AnnotationsConstants.CIRCLE,
            tool_type: TOOL_IDS.CIRCLE,
            title: titlePrefix() + getFormattedArea(Math.PI * radius * globalUpx * radius * globalUpx),
            extent: [center[0] - radius, center[1] - radius, center[0] + radius, center[1] + radius],
        });
    };

    /** Arrow.onDrawEnd: the arrow line plus a text "Label" polygon at its second vertex. */
    _saveArrow = (points) => {
        const line = this._dedupe(points);
        if (!this._saveLine(line, TOOL_IDS.ARROW)) return;
        const [x, y] = line[1];
        const ring = [[x, y + FIXED_ANNO_HEIGHT / 4], [x, y - FIXED_ANNO_HEIGHT / 4], [x, y - FIXED_ANNO_HEIGHT / 4]];
        store.dispatch(addAnnotation(this.props.mapId, {
            area: 0,
            perimeter: pathLength(ring) * this.props.slide.uperpixel,
            color: this.props.selectedDrawColor,
            new_bounds: JSON.stringify([ring]),
            slide: this.props.slide.id,
            creator: getLoggedInUserId(),
            shape: AnnotationsConstants.POLYGON,
            tool_type: AnnotationsConstants.TOOL_TYPE.LABEL,
            center: extentCenter(this._extentOf(ring)).toString(),
            anno_drawer: annotationsApp.drawer,
            created_z_level: this.props.viewer.getView().getZoom(),
            title: 'Label',
        }));
    };

    /** MagicTool: the chosen detector polygon is saved like any Polygon feature. */
    _saveMagicFeature = (feature) => {
        const geometry = feature.getGeometry();
        const coordinates = geometry.getCoordinates();
        const area = geometry.getArea();
        const upx = this.props.slide.uperpixel;
        this._save({
            area: area * upx * upx,
            perimeter: pathLength(coordinates[0]) * upx,
            new_bounds: JSON.stringify(coordinates),
            shape: AnnotationsConstants.POLYGON,
            tool_type: TOOL_IDS.MAGIC_TOOL,
            title: titlePrefix() + getFormattedArea(area * getUperpixel() ** 2),
            extent: geometry.getExtent(),
        });
    };

    // ------------------------------------------------------------------ tool handlers

    _onClickToolMove = (e) => this.setState({previewPoint: this._eventToOl(e)});

    _onLineClick = (e) => {
        if (e.button !== 0 || this._consumeJustPanned()) return;
        const point = this._eventToOl(e);
        const {points} = this.state;
        if (!points.length) return this.setState({points: [point], isDrawing: true});
        this._reset();
        this._saveLine([points[0], point], TOOL_IDS.LINE);
    };

    _onArrowClick = (e) => {
        if (e.button !== 0 || this._consumeJustPanned()) return;
        const point = this._eventToOl(e);
        const {points} = this.state;
        // OL finishes a LineString when its last point is clicked again, or at maxPoints.
        if (points.length >= 2 && this._screenDistance(point, points[points.length - 1]) <= SAME_POINT_PX) {
            this._reset();
            return this._saveArrow(points);
        }
        const next = [...points, point];
        if (next.length >= MAX_ARROW_POINTS) {
            this._reset();
            return this._saveArrow(next);
        }
        this.setState({points: next, isDrawing: true});
    };

    _onArrowDblClick = (e) => {
        e.preventDefault();
        const {points} = this.state;
        this._reset();
        if (points.length >= 2) this._saveArrow(points);
    };

    _onRectClick = (e) => {
        if (e.button !== 0 || this._consumeJustPanned()) return;
        const point = this._eventToOl(e);
        const {points} = this.state;
        if (!points.length) return this.setState({points: [point], isDrawing: true});
        const [x1, y1] = points[0];
        const [x2, y2] = point;
        this._reset();
        if (x1 !== x2 && y1 !== y2) this._savePolygon([[x1, y1], [x1, y2], [x2, y2], [x2, y1]], TOOL_IDS.RECTANGLE);
    };

    _onPolygonClick = (e) => {
        if (e.button !== 0 || this._consumeJustPanned()) return;
        const point = this._eventToOl(e);
        const {points} = this.state;
        // Clicking the first vertex closes the ring, as in OL.
        if (points.length >= 3 && this._screenDistance(point, points[0]) <= SAME_POINT_PX) {
            this._reset();
            return this._savePolygon(points, TOOL_IDS.POLYGON);
        }
        this.setState({points: [...points, point], isDrawing: true});
    };

    _onPolygonDblClick = (e) => {
        e.preventDefault();
        const {points} = this.state;
        this._reset();
        this._savePolygon(points, TOOL_IDS.POLYGON);
    };

    _onFreeDown = (e) => {
        if (e.button !== 0) return;
        this.setState({points: [this._eventToOl(e)], isDrawing: true});
    };

    _onFreeMove = (e) => {
        if (!this.state.isDrawing) return;
        const point = this._eventToOl(e);
        this.setState(s => ({points: [...s.points, point], previewPoint: point}));
    };

    _onFreeUp = () => {
        if (!this.state.isDrawing) return;
        const {points} = this.state;
        this._reset();
        if (this.props.activeTool === TOOL_IDS.FREEHAND) this._savePolygon(points, TOOL_IDS.FREEHAND);
        else this._saveLine(points, TOOL_IDS.FREELINE);
    };

    _onCircleDown = (e) => {
        if (e.button !== 0) return;
        e.preventDefault();
        const point = this._eventToOl(e);
        this.setState({points: [point], isDrawing: true, previewPoint: point});
    };

    _onCircleMove = (e) => {
        if (this.state.isDrawing) this.setState({previewPoint: this._eventToOl(e)});
    };

    _onCircleUp = (e) => {
        const {isDrawing, points, previewPoint} = this.state;
        if (!isDrawing || !points.length) return;
        const center = points[0];
        const end = previewPoint || this._eventToOl(e);
        const radius = Math.hypot(end[0] - center[0], end[1] - center[1]);
        this._reset();
        if (radius >= 1) this._saveCircle(center, radius);
    };

    _onMagicClick = (e) => {
        if (e.button !== 0 || this._consumeJustPanned()) return;
        const point = this._eventToOl(e);
        if (!this.state.isDrawing) {
            this.setState({points: [point], isDrawing: true, magicFeatures: [], magicPreviewFeature: null});
            store.dispatch(updateMagicToolDataRetrievalStatus(true));
            const {viewer, slide} = this.props;
            post('/api/magictool_detector/', querystring.stringify({
                start_point: JSON.stringify(point),
                // The detector reads tiles at this level; OSD zoom is fractional.
                z: Math.round(viewer.getView().getZoom()),
                slide: slide.id,
                extent: JSON.stringify(viewer.getView().calculateExtent(viewer.getSize())),
                user_id: getLoggedInUserId(),
            }))
                .then(getAnnotationsFeature)
                .then(features => {
                    store.dispatch(updateMagicToolDataRetrievalStatus(false));
                    this.setState({magicFeatures: features || []}, () => this._updateMagicPreview(this.state.previewPoint));
                })
                .catch(err => {
                    notifyCustomErrors(err);
                    store.dispatch(updateMagicToolDataRetrievalStatus(false));
                });
            return;
        }
        const feature = this.state.magicPreviewFeature;
        this.setState({...this.initialState(), cursorScreen: this.state.cursorScreen});
        if (feature) this._saveMagicFeature(feature);
    };

    /** MagicTool.onGeometryChange: every 150 px from the start picks the next detector result. */
    _updateMagicPreview = (point) => {
        const {isDrawing, magicFeatures, points} = this.state;
        if (!isDrawing || !magicFeatures.length || !point) return;
        const index = Math.round(Math.hypot(point[0] - points[0][0], point[1] - points[0][1]) / MAGIC_STEP_PX);
        this.setState({magicPreviewFeature: magicFeatures[index] || magicFeatures[magicFeatures.length - 1]});
    };

    _onMagicMove = (e) => {
        const point = this._eventToOl(e);
        this.setState({previewPoint: point});
        this._updateMagicPreview(point);
    };

    _getToolHandlers = () => {
        const click = {onMouseMove: this._onClickToolMove, onContextMenu: this._stopDrawing};
        switch (this.props.activeTool) {
            case TOOL_IDS.LINE:
                return {...click, onClick: this._onLineClick};
            case TOOL_IDS.ARROW:
                return {...click, onClick: this._onArrowClick, onDoubleClick: this._onArrowDblClick};
            case TOOL_IDS.RECTANGLE:
                return {...click, onClick: this._onRectClick};
            case TOOL_IDS.POLYGON:
                return {...click, onClick: this._onPolygonClick, onDoubleClick: this._onPolygonDblClick};
            case TOOL_IDS.MAGIC_TOOL:
                return {...click, onClick: this._onMagicClick, onMouseMove: this._onMagicMove};
            case TOOL_IDS.FREELINE:
            case TOOL_IDS.FREEHAND:
                return {onMouseDown: this._onFreeDown, onMouseMove: this._onFreeMove, onMouseUp: this._onFreeUp,
                    onContextMenu: this._stopDrawing};
            case TOOL_IDS.CIRCLE:
                return {onMouseDown: this._onCircleDown, onMouseMove: this._onCircleMove, onMouseUp: this._onCircleUp,
                    onContextMenu: this._stopDrawing};
            default:
                return {};
        }
    };

    // ------------------------------------------------------------------ preview

    _renderTooltip = (text, [x, y]) => text ? (
        <text x={x + 15} y={y - 15} fill="DarkBlue" pointerEvents="none"
              style={{fontSize: '20px', fontWeight: 'bold', textShadow: '0 0 5px white, 0 0 5px white, 0 0 5px white'}}>
            {text}
        </text>
    ) : null;

    _renderPreview = () => {
        const {activeTool, toScreen} = this.props;
        const {points, previewPoint, isDrawing, magicPreviewFeature} = this.state;
        if (!isDrawing || !points.length) return null;

        const upx = this.props.slide.uperpixel || getUperpixel();
        const formatLength = (length) => getFormattedLength(length * upx);
        const formatArea = (area) => getFormattedArea(area * upx * upx);
        const toPoints = (pts) => pts.map(toScreen).map(p => p.join(',')).join(' ');
        const [fx, fy] = toScreen(points[0]);
        const firstPoint = <circle cx={fx} cy={fy} r={7} fill={PREVIEW_BLUE} stroke="white" strokeWidth={3} pointerEvents="none"/>;
        const previewProps = {stroke: PREVIEW_BLUE, strokeWidth: PREVIEW_STROKE, pointerEvents: 'none'};

        switch (activeTool) {
            case TOOL_IDS.LINE:
            case TOOL_IDS.ARROW: {
                if (!previewPoint) return firstPoint;
                const all = [...points, previewPoint];
                return <>
                    {firstPoint}
                    <polyline points={toPoints(all)} fill="none" {...previewProps}/>
                    {this._renderTooltip(formatLength(pathLength(all)), toScreen(previewPoint))}
                </>;
            }
            case TOOL_IDS.FREELINE:
                if (points.length < 2) return null;
                return <>
                    <polyline points={toPoints(points)} fill="none" {...previewProps}/>
                    {this._renderTooltip(formatLength(pathLength(points)), toScreen(points[points.length - 1]))}
                </>;
            case TOOL_IDS.CIRCLE: {
                if (!previewPoint) return firstPoint;
                const radius = Math.hypot(previewPoint[0] - points[0][0], previewPoint[1] - points[0][1]);
                const [ex, ey] = toScreen([points[0][0] + radius, points[0][1]]);
                return <>
                    {firstPoint}
                    <circle cx={fx} cy={fy} r={Math.hypot(ex - fx, ey - fy)} fill={PREVIEW_FILL} {...previewProps}/>
                    {radius >= 1 && this._renderTooltip(formatArea(Math.PI * radius * radius), toScreen(previewPoint))}
                </>;
            }
            case TOOL_IDS.RECTANGLE: {
                if (!previewPoint) return firstPoint;
                const [x1, y1] = points[0];
                const [x2, y2] = previewPoint;
                const corners = [[x1, y1], [x1, y2], [x2, y2], [x2, y1]];
                return <>
                    {firstPoint}
                    <polygon points={toPoints(corners)} fill={PREVIEW_FILL} {...previewProps}/>
                    {this._renderTooltip(formatArea(Math.abs((x2 - x1) * (y2 - y1))), toScreen(previewPoint))}
                </>;
            }
            case TOOL_IDS.POLYGON:
            case TOOL_IDS.FREEHAND: {
                const all = [...points, previewPoint].filter(Boolean);
                const area = all.length >= 3 ? ringArea(all) : 0;
                return <>
                    {firstPoint}
                    <polyline points={toPoints(all)} fill={PREVIEW_FILL} {...previewProps}/>
                    {area > 0 && this._renderTooltip(formatArea(area), toScreen(all[all.length - 1]))}
                </>;
            }
            case TOOL_IDS.MAGIC_TOOL: {
                const ring = magicPreviewFeature?.getGeometry?.()?.getCoordinates?.()?.[0];
                return <>
                    {firstPoint}
                    {ring && <polygon points={toPoints(ring)} fill={PREVIEW_FILL} {...previewProps}/>}
                </>;
            }
            default:
                return null;
        }
    };

    // ------------------------------------------------------------------ render

    render() {
        const {containerWidth, containerHeight} = this.props;
        const handlers = this._getToolHandlers();

        const merged = {
            ...handlers,
            onMouseDown: (e) => {
                if (e.button === 0) this.setState({mightPan: true, panStart: [e.clientX, e.clientY]});
                handlers.onMouseDown?.(e);
            },
            onMouseMove: (e) => {
                this.setState({cursorScreen: this._eventScreen(e)});
                const {isPanning, mightPan, panStart, isDrawing} = this.state;
                if (isPanning) return;
                const drawingByDrag = DRAG_TOOLS.has(this.props.activeTool) && isDrawing;
                if (mightPan && panStart && !drawingByDrag &&
                    Math.hypot(e.clientX - panStart[0], e.clientY - panStart[1]) > PAN_THRESHOLD_PX) {
                    e.preventDefault();
                    e.stopPropagation();
                    this._startPan(e);
                    return;
                }
                handlers.onMouseMove?.(e);
            },
            onMouseUp: (e) => {
                if (this.state.isPanning) return;
                this.setState({mightPan: false, panStart: null});
                handlers.onMouseUp?.(e);
            },
            onMouseLeave: () => {
                this.setState(this.state.isPanning ? {cursorScreen: null} : {cursorScreen: null, mightPan: false, panStart: null});
            },
        };

        const {cursorScreen} = this.state;
        return (
            <>
                <rect ref={this._hitAreaRef} x={0} y={0} width={containerWidth} height={containerHeight}
                      fill="transparent" style={{cursor: 'none'}} {...merged}/>
                {cursorScreen && (
                    <circle cx={cursorScreen[0]} cy={cursorScreen[1]} r={7} fill={PREVIEW_BLUE} stroke="white"
                            strokeWidth={3} pointerEvents="none"/>
                )}
                {this._renderPreview()}
            </>
        );
    }
}

export default DrawInteractionOSD;
